// Signed-in dashboard route. Polls /v1/me every 2s, redirects to
// sign-in on 401, and gates between Loader → the Sidebar shell.
// Route state lives here so it survives polls.

import React from "react";
import { useAuth, useClerk, useUser } from "@clerk/clerk-react";
import { Brand } from "../components/Brand";
import { Button } from "../design/Button";
import { Loader } from "../components/Loader";
import { Sidebar } from "../shell/Sidebar";
import { useRoute } from "../shell/route";
import { Onboarding } from "./Onboarding";
import { clearWizard } from "../lib/onboarding";
import { useAuthHeaders } from "../lib/auth";
import {
  ackOnboarding,
  getMe,
  postSignupIntent,
  Unauthorized,
} from "../lib/api";
import { readCachedMe, writeCachedMe, clearCachedMe } from "../lib/meCache";
import { clearSignupIntent } from "../lib/intent";
import { landingUrl } from "../lib/urls";
import type { Me } from "../lib/types";

function Chrome({ children }: { children: React.ReactNode }) {
  const { signOut } = useClerk();
  const { isSignedIn } = useAuth();
  return (
    <div className="app-shell">
      <header className="app-header">
        <Brand />
        {isSignedIn && (
          <Button
            variant="ghost"
            icon="logout"
            onClick={() => {
              clearCachedMe();
              clearSignupIntent();
              signOut({ redirectUrl: landingUrl() });
            }}
          >
            Sign out
          </Button>
        )}
      </header>
      <main>
        <div className="content-pad center-page">{children}</div>
      </main>
    </div>
  );
}

export function Dashboard() {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const clerk = useClerk();
  const authHeaders = useAuthHeaders();
  const [me, setMe] = React.useState<Me | null>(null);
  const [error, setError] = React.useState("");
  const [route, navigate] = useRoute();
  const [orgIntent, setOrgIntent] = React.useState(false);
  const [onboardingDone, setOnboardingDone] = React.useState(false);
  // Latches true while a fresh account is being walked through setup. Kept
  // separate from live traffic so the final step can *show* the first session
  // landing instead of the dashboard snatching the user away the instant
  // `request_count` ticks past zero.
  const [onboardingActive, setOnboardingActive] = React.useState(false);
  // `/?setup` relaunches the wizard on demand (Overview's "Setup guide" link).
  const [rerunSetup] = React.useState(() =>
    new URLSearchParams(window.location.search).has("setup"),
  );
  React.useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("intent") === "org") {
      setOrgIntent(true);
      url.searchParams.delete("intent");
      window.history.replaceState(null, "", url.toString());
    }
  }, []);

  const refresh = React.useCallback(async () => {
    try {
      const data = await getMe(authHeaders);
      setMe(data);
      setError("");
    } catch (e) {
      if (e instanceof Unauthorized) {
        await clerk.redirectToSignIn({ redirectUrl: window.location.href });
        return;
      }
      throw e;
    }
  }, [authHeaders, clerk]);

  const clerkUserId = user?.id ?? null;
  React.useEffect(() => {
    if (me && clerkUserId) writeCachedMe(clerkUserId, me);
  }, [me, clerkUserId]);

  // Paint the shell from the last-known /v1/me while the live fetch runs,
  // so a reload shows real content instead of the distilling gate. The
  // background tick replaces this with fresh data within a poll.
  React.useEffect(() => {
    if (me || !clerkUserId) return;
    const cached = readCachedMe(clerkUserId);
    if (cached) setMe(cached);
  }, [me, clerkUserId]);

  React.useEffect(() => {
    if (!isSignedIn) return;
    if (me?.signup_intent) {
      if (me.signup_intent !== "org") setOrgIntent(false);
      return;
    }
    if (!orgIntent) return;
    // Org intent only applies to the freshly-created account whose signup
    // carried ?intent=org. An existing personal account that merely followed
    // an org link stays personal: anchor it as "single" so future org links
    // are ignored (first-write-wins).
    const createdAt = user?.createdAt?.getTime();
    const isFresh =
      createdAt != null && Date.now() - createdAt < 10 * 60 * 1000;
    if (isFresh) {
      void postSignupIntent(authHeaders, "org")
        .then(() => refresh())
        .catch(() => {});
      return;
    }
    void postSignupIntent(authHeaders, "single")
      .then(() => {
        setOrgIntent(false);
        return refresh();
      })
      .catch(() => {});
  }, [
    orgIntent,
    isSignedIn,
    authHeaders,
    me?.signup_intent,
    user?.createdAt,
    refresh,
  ]);

  React.useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      void clerk.redirectToSignIn({ redirectUrl: window.location.href });
      return;
    }
    let cancelled = false;
    let timer: number | null = null;
    async function tick() {
      try {
        await refresh();
        if (!cancelled) {
          timer = window.setTimeout(tick, 2000);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          timer = window.setTimeout(tick, 5000);
        }
      }
    }
    void tick();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [isLoaded, isSignedIn, clerk, refresh]);

  // First-run onboarding gate. Shown exactly once, and only to a genuinely
  // new account on its first dashboard visit: created within the onboarding
  // window, not yet acked, and with no traffic yet. The moment it opens we
  // ack `onboarding_complete` so it never returns — even if the user closes
  // the tab mid-flow — while the `onboardingActive` latch keeps it on screen
  // for the rest of this session. Must run before the loading guards below so
  // the hook order stays stable across the null→loaded transition; it waits
  // for the real /me (meLoaded) so a slow load never acks a returning user.
  const ONBOARDING_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
  const accountCreatedMs = user?.createdAt?.getTime();
  const isNewAccount =
    accountCreatedMs != null &&
    Date.now() - accountCreatedMs < ONBOARDING_WINDOW_MS;
  const meLoaded = me != null;
  const ackedOnboarding = me?.onboarding?.onboarding_complete === true;
  const hasTraffic = (me?.usage?.request_count ?? 0) > 0;
  React.useEffect(() => {
    if (onboardingDone || onboardingActive || !meLoaded) return;
    if (rerunSetup) {
      clearWizard();
      setOnboardingActive(true);
      return;
    }
    if (isNewAccount && !ackedOnboarding && !hasTraffic) {
      setOnboardingActive(true);
      void ackOnboarding(authHeaders, "onboarding_complete").catch(() => {});
    }
  }, [
    rerunSetup,
    meLoaded,
    isNewAccount,
    ackedOnboarding,
    hasTraffic,
    onboardingDone,
    onboardingActive,
    authHeaders,
  ]);

  if (error && !me) {
    return (
      <Chrome>
        <p className="section-sub">Could not load your account.</p>
        <Button
          onClick={() =>
            void refresh().catch(() => setError("Could not load your account."))
          }
        >
          Retry
        </Button>
      </Chrome>
    );
  }
  if (!isLoaded || !me) {
    return (
      <Chrome>
        <Loader />
      </Chrome>
    );
  }

  // Surface the Clerk email on the Me object if the API hasn't yet.
  const meWithEmail: Me = me.email
    ? me
    : { ...me, email: user?.primaryEmailAddress?.emailAddress || null };

  const showOnboarding = !onboardingDone && onboardingActive;

  if (showOnboarding) {
    return (
      <Onboarding
        me={meWithEmail}
        authHeaders={authHeaders}
        refresh={refresh}
        onComplete={() => {
          setOnboardingDone(true);
          setOnboardingActive(false);
          if (rerunSetup) {
            window.history.replaceState(null, "", window.location.pathname);
          }
        }}
      />
    );
  }

  return (
    <Sidebar
      route={route}
      navigate={navigate}
      me={meWithEmail}
      org={meWithEmail.organizations?.[0] ?? null}
      authHeaders={authHeaders}
      refresh={refresh}
    />
  );
}
